import SoqlDataProvider from 'common/visualizations/dataProviders/SoqlDataProvider';
import type { View } from 'common/types/view';
import SoqlHelpers from 'common/visualizations/helpers/SoqlHelpers';
import { type Vif } from 'common/visualizations/vif';
import _ from 'lodash';
import { type ClientContextVariable } from 'common/types/clientContextVariable';
import { migrateVif } from 'common/visualizations/helpers/migrateVif';
import { type ViewColumn } from 'common/types/viewColumn';
import { AG_SOQL_EXPORT_QUERY_LIMIT } from 'common/visualizations/helpers/TableDataExportHelpers';

const LARGE_COUNT_THRESHOLD = 999999;

export interface ExportStateFunctions {
  setRowCountFiltered: (count: number) => void;
}

export const filterRowData = (
  view: View,
  exportFunctions: ExportStateFunctions,
  queryString: string,
  clientContextVariables: ClientContextVariable[],
) => {
  const domain = window.location.hostname;
  const datasetUid = view.id;
  if (domain && datasetUid) {
    const soqlDataProvider = new SoqlDataProvider({ domain, datasetUid, clientContextVariables }, true);
    soqlDataProvider.getRowCountForQuery(queryString).then((resp) => {
      exportFunctions.setRowCountFiltered(resp);
    });
  }
};

export const getTotalRowCount = (
  view: View,
  setRowCountAll: (count: number) => void,
  clientContextVariables: ClientContextVariable[],
) => {
  const domain = window.location.hostname;
  const datasetUid = view.id;
  if (domain && datasetUid) {
    const soqlDataProvider = new SoqlDataProvider({ domain, datasetUid, clientContextVariables }, true);
    soqlDataProvider.getRowCount('').then(function (resp) {
      setRowCountAll(resp);
    });
  }
};

// This will only work with a vif from an AG-TABLE otherwise will return a *
const getSelectFromVif = (vif: Vif): string => {
  if (!vif?.series?.[0] || vif.series[0]?.type !== 'agTable') {
    return '*';
  }

  const visibleColumns = vif.series[0]?.dataSource.dimension?.columns || [];
  const columnClause = _.compact(
    visibleColumns.map((column: ViewColumn) =>
      !column.hide && !(column.flags || []).includes('hidden') ? column.fieldName : undefined,
    ),
  ).join(', ');
  return _.isEmpty(columnClause) ? '*' : columnClause;
};

const getWhereFromVif = (vif: Vif) => {
  const filters = vif.series[0]?.dataSource?.filters || [];
  return _.compact(filters.map(SoqlHelpers.filterToWhereClauseComponent)).join(' AND ');
};

const convertAscending = (ascending: boolean) => (ascending ? 'ASC' : 'DESC');

export const getOrderByFromVif = (vif: Vif) => {
  const order = vif?.configuration?.order || [];
  // The vif seems to add :id when there is no sort.
  // This is problematic because its not an order by that a user has added,
  // so removing it from the array here if present.
  _.remove(order, (v) => v.columnName === ':id');

  if (!order || !order.length) {
    return;
  }

  const orderString = order.map((v) => `${v.columnName} ${convertAscending(!!v.ascending)}`).join(', ');

  return orderString;
};

export const getSearchFromVif = (vif: Vif) =>
  // @ts-expect-error TS(2339) search string is not on the vif type but it's there
  vif?.series?.[0]?.dataSource?.searchString || undefined;

export const getQueryStringFromVif = (vif: Vif) => {
  const cleanVif = migrateVif(vif);
  const selectClause = getSelectFromVif(cleanVif);
  const whereClause = getWhereFromVif(cleanVif);
  const orderBy = getOrderByFromVif(cleanVif);
  const searchClause = getSearchFromVif(cleanVif);

  let queryString = `SELECT ${selectClause}`;
  if (whereClause) {
    queryString += ` WHERE ${whereClause}`;
  }
  if (orderBy) {
    queryString += ` ORDER BY ${orderBy}`;
  }
  if (searchClause) {
    queryString += ` SEARCH "${searchClause}"`;
  }
  return queryString;
};

const getNonSoqlOrderBy = (view: View) => {
  // this is where order bys are stored on default views
  const order = view.metadata?.jsonQuery?.order;
  if (!order || !order.length) {
    return;
  }

  const orderString = order
    .map((v: any) => `${v.columnFieldName} ${convertAscending(!!v.ascending)}`)
    .join(', ');
  return ` ORDER BY ${orderString}`;
};

// a basic select of all the non-hidden columns on the view
// plus an order by if there is one stored in the jsonQuery object (default views only)
export const getQueryStringFromView = (view: View) => {
  let columns = view.columns;
  let queryString;
  if (columns) {
    // filter out hidden columns
    columns = _.filter(columns, (v) => !v.flags?.includes('hidden'));
    // extract field name and create string
    queryString = _.map(columns, (v) => v.fieldName).join(', ');

    // get order by
    const orderByString = getNonSoqlOrderBy(view);

    return `SELECT ${queryString}${orderByString || ''}`;
  }
};

export const shouldRenderWarning = (
  downloadType: string | undefined,
  selectedFiltered: boolean,
  rowCountFiltered: number | undefined,
  rowCountAll: number | undefined,
) => {
  const count = selectedFiltered ? rowCountFiltered : rowCountAll;
  return downloadType === 'xlsx' && count !== undefined && count > AG_SOQL_EXPORT_QUERY_LIMIT;
};

// A helper function to determine whether the selected download option exceeds threshold.
export const shouldRenderLargeDatasetWarning = (
  selectedFiltered: boolean,
  rowCountFiltered: number | undefined,
  rowCountAll: number | undefined,
  threshold = LARGE_COUNT_THRESHOLD,
) => {
  const count = selectedFiltered ? rowCountFiltered : rowCountAll;
  return count !== undefined && count > threshold;
};
